var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { checkUserType } from "healthex-common/checkusertype";
import { StudyUserInput } from "healthex-common/study.dtos";
import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { computed, observable, runInAction } from "mobx";
import { FormModel } from "../FormModel";
import { SimpleDialogModel } from "../SimpleDialogModel";
import { addedUserDisplayName, usersMissingResearcherPermission } from "../StudyUsersFormModel";
const TEAM_MEMBER_SEARCH_MIN_LENGTH = 2;
const TEAM_MEMBER_SEARCH_PAGE_SIZE = 50;
export class GlobalAdminProjectTeamMembersModel {
    constructor(opts) {
        this.loadGeneration = 0;
        this.addUsersFormModel = new FormModel({ users: [] }, []);
        this.teamMembers = [];
        this.teamMembersLoading = false;
        this.teamMembersLoadFailed = false;
        this.addUsersErrors = new Set();
        this.addUserErrorMessage = null;
        this.removeUserErrorMessage = null;
        this.actionBusy = false;
        this.confirmRemoveDialog = new SimpleDialogModel();
        this.confirmRemoveUser = null;
        this.globalAdminProjectsManager = opts.container.globalAdminProjectsManager;
        this.provisioningService = opts.container.provisioningService;
        this.authManager = opts.container.authManager;
        this.organizationId = opts.organizationId;
        this.projectId = opts.projectId;
        void this.loadTeamMembers();
    }
    get addButtonEnabled() {
        return this.addUsersFormModel.input.users.length > 0 && !this.actionBusy;
    }
    get addUsersHasError() {
        return this.addUserErrorMessage !== null;
    }
    get addUsersHelperText() {
        var _a;
        return (_a = this.addUserErrorMessage) !== null && _a !== void 0 ? _a : "Type at least 2 characters of a name or email to search.";
    }
    get removeUserErrorText() {
        return this.removeUserErrorMessage;
    }
    async loadTeamMembers() {
        const generation = ++this.loadGeneration;
        runInAction(() => {
            this.teamMembersLoading = true;
            this.teamMembersLoadFailed = false;
        });
        try {
            const teamMembers = await this.globalAdminProjectsManager.listProjectUsers(this.organizationId, this.projectId);
            if (generation !== this.loadGeneration) {
                return;
            }
            runInAction(() => {
                this.teamMembers = teamMembers;
                this.teamMembersLoading = false;
                this.teamMembersLoadFailed = false;
            });
        }
        catch {
            if (generation !== this.loadGeneration) {
                return;
            }
            runInAction(() => {
                this.teamMembersLoading = false;
                this.teamMembersLoadFailed = true;
            });
        }
    }
    async getMatchingUser(nameOrEmailPrefix) {
        const trimmedPrefix = nameOrEmailPrefix.trim();
        if (trimmedPrefix.length < TEAM_MEMBER_SEARCH_MIN_LENGTH) {
            return [];
        }
        const existingUserIds = new Set(this.teamMembers.map((member) => member.user.id));
        const { results } = await this.provisioningService.getUsersByOrganization(this.organizationId, trimmedPrefix, 0, TEAM_MEMBER_SEARCH_PAGE_SIZE, [UserPermissions.RESEARCHER]);
        return results
            .filter((user) => !existingUserIds.has(user.id))
            .map((user) => {
            const validUser = checkUserType(user, UserType.HEALTHCARE_ORGANIZATION, UserType.EXTERNAL_RESEARCHER);
            if (validUser === null) {
                return null;
            }
            return {
                user: validUser,
                display: addedUserDisplayName(validUser, trimmedPrefix),
            };
        })
            .filter((addedUser) => addedUser !== null)
            .sort((a, b) => a.display.localeCompare(b.display));
    }
    async addSelectedUsers() {
        this.addUsersErrors.clear();
        this.addUserErrorMessage = null;
        const invalidRoles = usersMissingResearcherPermission(this.addUsersFormModel.input.users);
        if (invalidRoles.length > 0) {
            this.addUserErrorMessage = "Please ensure the team members have Researcher permissions.";
            invalidRoles.forEach((user) => this.addUsersErrors.add(user.user.id));
            return;
        }
        const usersToAdd = [...this.addUsersFormModel.input.users];
        const failedUsers = [];
        runInAction(() => {
            this.actionBusy = true;
        });
        try {
            const outcomes = await Promise.allSettled(usersToAdd.map((userToAdd) => this.globalAdminProjectsManager.addProjectUser(this.organizationId, this.projectId, userToAdd.user.id, new StudyUserInput({}))));
            outcomes.forEach((outcome, index) => {
                if (outcome.status === "rejected") {
                    failedUsers.push(usersToAdd[index]);
                }
            });
            runInAction(() => {
                this.addUsersFormModel.updateInput({ users: failedUsers });
                if (failedUsers.length > 0) {
                    this.addUserErrorMessage = "Unable to add team member";
                }
            });
        }
        finally {
            runInAction(() => {
                this.actionBusy = false;
            });
            if (failedUsers.length < usersToAdd.length) {
                await this.loadTeamMembers();
            }
        }
    }
    async removeTeamMember(user) {
        if (!this.canRemoveUser(user)) {
            return;
        }
        this.removeUserErrorMessage = null;
        this.confirmRemoveUser = user;
        const confirmed = await this.confirmRemoveDialog.showAndWait();
        if (!confirmed) {
            this.confirmRemoveUser = null;
            return;
        }
        runInAction(() => {
            this.actionBusy = true;
        });
        try {
            await this.globalAdminProjectsManager.removeProjectUser(this.organizationId, this.projectId, user.user.id);
            await this.loadTeamMembers();
        }
        catch {
            runInAction(() => {
                this.removeUserErrorMessage = "Unable to remove team member";
            });
        }
        finally {
            runInAction(() => {
                this.actionBusy = false;
                this.confirmRemoveUser = null;
            });
        }
    }
    isMe(userId, email) {
        if (this.authManager.id === userId) {
            return true;
        }
        const authEmail = this.authManager.email;
        if (authEmail && email && authEmail.toLowerCase() === email.toLowerCase()) {
            return true;
        }
        return false;
    }
    canRemoveUser(studyUser) {
        return !this.isMe(studyUser.user.id, studyUser.user.email);
    }
}
__decorate([
    observable
], GlobalAdminProjectTeamMembersModel.prototype, "teamMembers", void 0);
__decorate([
    observable
], GlobalAdminProjectTeamMembersModel.prototype, "teamMembersLoading", void 0);
__decorate([
    observable
], GlobalAdminProjectTeamMembersModel.prototype, "teamMembersLoadFailed", void 0);
__decorate([
    observable
], GlobalAdminProjectTeamMembersModel.prototype, "addUsersErrors", void 0);
__decorate([
    observable
], GlobalAdminProjectTeamMembersModel.prototype, "addUserErrorMessage", void 0);
__decorate([
    observable
], GlobalAdminProjectTeamMembersModel.prototype, "removeUserErrorMessage", void 0);
__decorate([
    observable
], GlobalAdminProjectTeamMembersModel.prototype, "actionBusy", void 0);
__decorate([
    observable
], GlobalAdminProjectTeamMembersModel.prototype, "confirmRemoveUser", void 0);
__decorate([
    computed
], GlobalAdminProjectTeamMembersModel.prototype, "addButtonEnabled", null);
__decorate([
    computed
], GlobalAdminProjectTeamMembersModel.prototype, "addUsersHasError", null);
__decorate([
    computed
], GlobalAdminProjectTeamMembersModel.prototype, "addUsersHelperText", null);
__decorate([
    computed
], GlobalAdminProjectTeamMembersModel.prototype, "removeUserErrorText", null);
